/**
 * PatientReceiptRowActions — per-row kebab for the Patient Billing → Advance
 * receipts sub-tab table. Ports Flutter
 * `patient_advance_receipts_screen.dart` `menuOptions()` (:435-504).
 *
 * Menu shape (Flutter):
 *   1. View Receipt                       — always
 *   2. Refund Payment                     — only if receiptAmount > 0
 *
 * "View Receipt" opens the receipt PDF via the billing-module
 * `usePrintReceipt` hook (`GET /Billing/Invoice/PrintReceipts/{receiptId}`).
 * Flutter renders an on-screen `ReceiptPopUp` with a Print button; the React
 * idiom collapses the two to the printable receipt document (the PDF *is*
 * the receipt view). A dedicated on-screen receipt organism is a follow-up.
 *
 * "Refund Payment" opens `PatientRefundPaymentDialog` (advance-receipt
 * refund, with the mandatory reason picker + min(receiptAmount,
 * availableBalance) clamp).
 */

import { useState } from 'react';
import { Eye, MoreHorizontal, Undo2, type LucideIcon } from 'lucide-react';
import { RowActionsTrigger } from '../row-actions-trigger';
import { AnchoredPopover, AnchoredPopoverContent } from '@scripsteam/scrips-signal-ds';
import { usePrintReceipt } from '@/billing/services/use-invoice-actions';
import { PatientRefundPaymentDialog } from './patient-refund-payment-dialog';
import type { AdvancePaymentReceipt } from '@/shared/types/billing';

export interface PatientReceiptRowActionsProps {
  row: AdvancePaymentReceipt;
  patientId: string;
}

export function PatientReceiptRowActions({
  row,
  patientId,
}: PatientReceiptRowActionsProps) {
  // PROD-2743 · switch ON: the DS `more` glyph, as the patients list row actions (#548). OFF = today.
  const [menuOpen, setMenuOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const printReceipt = usePrintReceipt();

  // Flutter gates Refund purely on `receiptAmount > 0` (:475) — match it
  // verbatim; do not add extra status gating (no invented defaults).
  const canRefund = (row.receiptAmount ?? 0) > 0;

  const open = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  const viewReceipt = () => {
    if (!row.id) return;
    printReceipt.mutate({
      receiptId: row.id,
      invoiceId: row.invoiceId ?? undefined,
    });
  };

  return (
    <>
      <div className="flex items-center justify-end">
        <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
          <RowActionsTrigger ariaLabel="Receipt actions" tooltip={`Actions for receipt ${row.receiptNumber ?? ''}`.trim()}>
            <MoreHorizontal className="size-4" aria-hidden />
          </RowActionsTrigger>
          <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-48 p-1 gap-3">
            <MenuItem
              icon={Eye}
              label="View Receipt"
              onClick={open(viewReceipt)}
            />
            {canRefund ? (
              <MenuItem
                icon={Undo2}
                label="Refund Payment"
                onClick={open(() => setRefundOpen(true))}
              />
            ) : null}
          </AnchoredPopoverContent>
        </AnchoredPopover>
      </div>

      {canRefund && (
        <PatientRefundPaymentDialog
          open={refundOpen}
          onOpenChange={setRefundOpen}
          receipt={row}
          patientId={patientId}
        />
      )}
    </>
  );
}

function MenuItem({
  icon: Glyph,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded px-3 py-1.5 text-left text-sm text-text-primary hover:bg-grey-100"
    >
      <Glyph className="size-4" aria-hidden />
      <span>{label}</span>
    </button>
  );
}
